import { useState, useEffect } from "react";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Trash2, Plus } from "lucide-react";
import { useConfirm } from "@/lib/confirm";
import { todayKey, fmtDayKey } from "@/lib/time";

// Fleet stores the week as "0".."6" = Monday..Sunday; null = day off.
export const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
export const fmtShift = (s) => (s?.start && s?.end ? `${s.start}–${s.end}` : "Off");
const field = "rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-600 disabled:opacity-40";
const deny = (e, msg) => (e.response?.status === 403 ? "You don't have permission to change shifts." : fleetError(e, msg));

export default function ShiftEditDialog({ driver, open, onOpenChange, onSaved }) {
  const [week, setWeek] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [saving, setSaving] = useState(false);
  const ask = useConfirm();

  const loadOverrides = (id) => fleetApi.get(`/drivers/${id}/schedule`).then((r) => setOverrides(r.data.overrides || [])).catch(() => setOverrides([]));

  useEffect(() => {
    if (!open || !driver) return;
    const t = driver.shift_template || {};
    setWeek(DAYS.map((_, i) => ({ on: !!t[i], start: t[i]?.start || "06:00", end: t[i]?.end || "18:00" })));
    loadOverrides(driver.id);
  }, [open, driver]);

  if (!driver) return null;
  const setDay = (i, patch) => setWeek((w) => w.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  const copyMonday = () => setWeek((w) => w.map((d, j) => (j > 0 && j < 5 ? { ...w[0] } : d)));
  const bad = week.find((d) => d.on && (!d.start || !d.end || d.start === d.end));

  const save = async () => {
    if (bad) { toast.error("Each working day needs a start and end time."); return; }
    if (!(await ask({ title: `Save ${driver.full_name || driver.name}'s weekly shifts?`, description: "AI will only assign trips inside these hours from now on.", confirmLabel: "Save shifts" }))) return;
    setSaving(true);
    try {
      const template = Object.fromEntries(week.map((d, i) => [String(i), d.on ? { start: d.start, end: d.end } : null]));
      await fleetApi.put(`/drivers/${driver.id}/shift-template`, { template });
      toast.success("Shifts saved.");
      onSaved?.(); onOpenChange(false);
    } catch (e) { toast.error(deny(e, "Couldn't save shifts.")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-slate-800 bg-[#0f172a] text-white sm:max-w-xl" data-testid="shift-edit-dialog">
        <DialogHeader>
          <DialogTitle>Shifts · {driver.full_name || driver.name}</DialogTitle>
          <DialogDescription className="text-slate-400">Set the usual week, then add one-day changes for time off or different hours.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {week.map((d, i) => (
            <div key={i} className="flex flex-wrap items-center gap-3 rounded-md border border-slate-800 bg-slate-900/50 px-3 py-2" data-testid={`shift-day-${i}`}>
              <label className="flex w-32 items-center gap-2 text-sm">
                <input type="checkbox" checked={d.on} onChange={(e) => setDay(i, { on: e.target.checked })} className="h-4 w-4 accent-blue-600" data-testid={`shift-day-on-${i}`} />
                {DAYS[i]}
              </label>
              <input type="time" value={d.start} disabled={!d.on} onChange={(e) => setDay(i, { start: e.target.value })} className={field} data-testid={`shift-day-start-${i}`} />
              <span className="text-slate-500">to</span>
              <input type="time" value={d.end} disabled={!d.on} onChange={(e) => setDay(i, { end: e.target.value })} className={field} data-testid={`shift-day-end-${i}`} />
              {!d.on && <span className="text-xs text-slate-500">Off</span>}
            </div>
          ))}
          <button onClick={copyMonday} className="text-xs font-medium text-blue-300 hover:underline" data-testid="shift-copy-monday">Copy Monday to Tue–Fri</button>
        </div>
        <Overrides driver={driver} overrides={overrides} reload={() => loadOverrides(driver.id)} ask={ask} />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300 hover:bg-slate-800 hover:text-white" data-testid="shift-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="gap-2 bg-blue-700 text-white hover:bg-blue-800" data-testid="shift-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save weekly shifts
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Overrides({ driver, overrides, reload, ask }) {
  const [f, setF] = useState({ date: "", off: true, start: "06:00", end: "14:00", note: "" });
  const [busy, setBusy] = useState(false);
  const upcoming = overrides.filter((o) => (o.date || "") >= todayKey()).sort((a, b) => a.date.localeCompare(b.date));

  const add = async () => {
    if (!f.date) { toast.error("Pick a date."); return; }
    if (!f.off && (!f.start || !f.end || f.start === f.end)) { toast.error("Enter start and end times."); return; }
    setBusy(true);
    try {
      await fleetApi.post("/shift-overrides", { driver_id: driver.id, date: f.date, off: f.off, start: f.off ? "" : f.start, end: f.off ? "" : f.end, note: f.note.trim() });
      toast.success(f.off ? `Day off added for ${fmtDayKey(f.date)}.` : `Hours changed for ${fmtDayKey(f.date)}.`);
      setF({ ...f, date: "", note: "" }); reload();
    } catch (e) { toast.error(deny(e, "Couldn't add the change.")); }
    finally { setBusy(false); }
  };
  const remove = async (o) => {
    if (!(await ask({ title: `Remove the change on ${fmtDayKey(o.date)}?`, description: "The driver goes back to their usual hours that day.", confirmLabel: "Remove", destructive: true }))) return;
    try { await fleetApi.delete(`/shift-overrides/${o.id}`); toast.success("Change removed."); reload(); }
    catch (e) { toast.error(deny(e, "Couldn't remove it.")); }
  };

  return (
    <div className="mt-2 space-y-2 border-t border-slate-800 pt-4" data-testid="shift-overrides">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">One-day changes</p>
      {upcoming.map((o) => (
        <div key={o.id} className="flex items-center justify-between rounded-md border border-slate-800 bg-slate-900/50 px-3 py-2 text-sm" data-testid={`shift-override-${o.id}`}>
          <span>{fmtDayKey(o.date)} · <b>{o.off ? "Day off" : fmtShift(o)}</b>{o.note ? <span className="text-slate-400"> · {o.note}</span> : null}</span>
          <button onClick={() => remove(o)} className="text-slate-400 hover:text-red-300" aria-label="Remove change" data-testid={`shift-override-delete-${o.id}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      {!upcoming.length && <p className="text-xs text-slate-500" data-testid="shift-overrides-empty">No upcoming changes.</p>}
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-slate-700 p-2">
        <input type="date" min={todayKey()} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={field} data-testid="shift-override-date" />
        <select value={f.off ? "off" : "hours"} onChange={(e) => setF({ ...f, off: e.target.value === "off" })} className={field} data-testid="shift-override-type">
          <option value="off">Day off</option><option value="hours">Different hours</option>
        </select>
        {!f.off && <>
          <input type="time" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className={field} data-testid="shift-override-start" />
          <input type="time" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className={field} data-testid="shift-override-end" />
        </>}
        <input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Note (optional)" className={`${field} min-w-0 flex-1`} data-testid="shift-override-note" />
        <Button onClick={add} disabled={busy} size="sm" className="gap-1 bg-slate-700 text-white hover:bg-slate-600" data-testid="shift-override-add">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add
        </Button>
      </div>
    </div>
  );
}
